import { useState, useEffect } from 'react';
import { PlatformSiteAri } from '@atlassian/ari/platform/site';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { useEmbeddedPageTracking } from '@atlassian/jira-issue-create-confluence-content/src/controllers/use-embedded-page-tracking/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';

const USER_ADD_PRODUCT_PERMISSION = 'userAddProductPermission';
export const fetchUserAddProductPermissions = async (cloudId: string) => {
	const response = await fetchJson('/gateway/api/permissions/permitted', {
		method: 'POST',
		body: JSON.stringify({
			resourceId: PlatformSiteAri.create({
				siteId: cloudId,
			}).toString(),
			permissionId: 'add-products',
		}),
		headers: {
			Accept: 'application/json',
		},
	});
	return response;
};

type Props = {
	embeddedConfluenceSource?: string;
	skip?: boolean;
};
export const useUserHasAddProductPermissions = ({
	embeddedConfluenceSource,
	skip = false,
}: Props) => {
	const { cloudId } = useTenantContext();
	const isSiteAdmin = useIsSiteAdmin();

	const [status, setStatus] = useState<{
		canProvisionConfluence: boolean;
		fetchStatus: 'pending' | 'resolved' | 'failed';
	}>({ canProvisionConfluence: false, fetchStatus: 'pending' });
	const fireAnalyticsEvent = useEmbeddedPageTracking();

	useEffect(() => {
		if (skip) {
			return;
		}

		if (isSiteAdmin) {
			// Shortcut - Site admins have add-product permissions
			setStatus({ canProvisionConfluence: true, fetchStatus: 'resolved' });
			return;
		}

		const getAddProductPermissions = async () => {
			try {
				const response = await fetchUserAddProductPermissions(cloudId);
				setStatus({ canProvisionConfluence: response?.permitted, fetchStatus: 'resolved' });
				fireAnalyticsEvent({
					action: 'success',
					eventName: USER_ADD_PRODUCT_PERMISSION,
					embeddedConfluenceSource,
				});
			} catch (error: unknown) {
				setStatus({ canProvisionConfluence: false, fetchStatus: 'failed' });
				fireErrorAnalytics({
					meta: {
						id: USER_ADD_PRODUCT_PERMISSION,
						packageName: 'jiraIssueViewBase',

						teamName: 'starburst',
					},
					attributes: { embeddedConfluenceSource },
					error: new Error(`Failed to get ${USER_ADD_PRODUCT_PERMISSION} status: ${error}`),
				});
			}
		};
		getAddProductPermissions();
	}, [cloudId, embeddedConfluenceSource, fireAnalyticsEvent, isSiteAdmin, skip]);

	return status;
};
